/**
 * Pure logic for rendering and editing the tractor-config record.
 *
 * Everything here is driven by the schema the API serves. There is deliberately
 * no list of field names, no enum vocabulary, and no group whitelist — a field
 * added to `tractor_config.py` reaches the console through these functions
 * unchanged.
 *
 * On validation: this mirrors only what the schema *declares* — the `type`
 * contract (a url is https, a date is ISO, an int is an integer) and
 * `max_length`. It does not re-encode limits the schema keeps to itself, such
 * as the backend's cap on list entries. Those stay server-side and surface as
 * the API's own 400. Client validation exists to save a round trip on an
 * obvious mistake, never to be a second, drifting copy of the rules.
 */
import {
  AttrValue,
  ChangeSet,
  ConfigSchema,
  SchemaField,
  SchemaGroup,
  SchemaOption,
  SchemaRule,
} from "./configTypes";
import { Vehicle } from "./data";

/**
 * What a form control holds. Always string-shaped, because that is what inputs
 * produce; `fromDraft` converts to the wire type on the way out. Keeping the
 * two representations distinct is what stops "0" and 0, or "" and null, from
 * being compared against each other during dirty-checking.
 */
export type DraftValue = string | string[];
export type Draft = Record<string, DraftValue>;

// ---------------------------------------------------------------------------
// Grouping and ordering
// ---------------------------------------------------------------------------

export interface FieldGroup {
  key: string;
  label: string;
  /** A sentence under the heading, when the schema supplies one. */
  note?: string;
  fields: SchemaField[];
}

/**
 * How a group's heading reads.
 *
 * The schema's own `groups` entry wins when there is one — that is where a
 * heading that isn't the key's title-case lives ("Tractor Update Status"), and
 * where a deliberately *empty* heading is expressed. Everything else is derived:
 * group keys are lower-case words ("capability", "roster"), so a title-case is
 * a faithful label, and deriving rather than looking up is what keeps a group
 * added to tractor_config.py readable with no change on this side.
 *
 * An empty string from the schema is honoured as an empty heading. Only an
 * *absent* entry falls back to derivation, so "no heading" and "no opinion"
 * stay different answers.
 */
export const groupLabel = (key: string, groups?: SchemaGroup[]): string => {
  const declared = groups?.find((g) => g.key === key);
  if (declared) return declared.label;
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Schema fields bucketed by group, both the groups and the fields within them
 * in the backend's `order`. Group position follows its earliest field, so the
 * sparse ordering in tractor_config.py is the single source of layout — the
 * schema's `groups` supplies headings only, and cannot move a group.
 */
export function groupFields(
  fields: SchemaField[],
  filter?: (f: SchemaField) => boolean,
  groups?: SchemaGroup[],
): FieldGroup[] {
  const buckets = new Map<string, SchemaField[]>();
  for (const field of fields) {
    if (filter && !filter(field)) continue;
    const bucket = buckets.get(field.group);
    if (bucket) bucket.push(field);
    else buckets.set(field.group, [field]);
  }
  return [...buckets.entries()]
    .map(([key, groupedFields]) => ({
      key,
      label: groupLabel(key, groups),
      note: groups?.find((g) => g.key === key)?.note,
      fields: [...groupedFields].sort((a, b) => a.order - b.order),
    }))
    .sort((a, b) => a.fields[0].order - b.fields[0].order);
}

// ---------------------------------------------------------------------------
// What may be edited
// ---------------------------------------------------------------------------

/**
 * Fields this console will not offer an edit control for, whatever the schema
 * says about them.
 *
 * The one deliberate exception to the no-vocabulary rule at the top of this
 * module, because a tractor's identity is a product invariant rather than a
 * per-deploy setting. The Jira asset id is derived from `display_name` and the
 * customer's own systems join on `customer_asset_id`, so a rename made here
 * silently re-points those joins.
 *
 * The backend enforces the same rule — `Field.fixed_after_create` in
 * `tractor_config.py`, refused by `validate_patch` with a 400. That is the
 * authority; this is the affordance. A write the API will reject is a control
 * that should never have been on screen, and the schema only starts saying so
 * on the next deploy of the lambda, so the console does not wait to be told.
 *
 * Two things to know before editing this set:
 *
 *   * it **overrides** the schema. A future `can_edit: true` on one of these
 *     still yields no control, so genuinely making one editable means changing
 *     this file as well as `tractor_config.py`.
 *   * it keys on the field *name*, so renaming a field server-side drops the
 *     guard silently. `fixed_after_create` is the durable half of the pair —
 *     this set only covers the window before it is deployed.
 */
const FIXED_AT_ONBOARDING: ReadonlySet<string> = new Set([
  "display_name",
  "customer_asset_id",
]);

/** Settable when the tractor is onboarded, and never again afterwards. */
export const isFixedAfterCreate = (field: SchemaField): boolean =>
  Boolean(field.fixed_after_create) || FIXED_AT_ONBOARDING.has(field.name);

/**
 * Whether the console offers a control for this field on an existing record.
 *
 * The single answer to that question: draft seeding, the diff, validation and
 * the rendered control all ask it, so a field cannot be inert in the form while
 * still riding along in the PATCH.
 */
export const isEditable = (field: SchemaField): boolean =>
  field.can_edit && !isFixedAfterCreate(field);

/**
 * Whether this field can be supplied while onboarding a tractor.
 *
 * `isEditable` answers the other question — may it be changed on the row as it
 * stands — and the two differ on identity, which is settable once and fixed
 * afterwards. This is the half that stays *yes*: onboarding is where a display
 * name comes from, so gating it on the edit rule would make it unsettable
 * anywhere. Falls back to `can_edit` for an API that predates the split.
 */
export const canSetOnCreate = (field: SchemaField): boolean =>
  field.can_set_on_create ?? field.can_edit;

export const fieldByName = (
  schema: ConfigSchema | null,
  name: string,
): SchemaField | undefined => schema?.fields.find((f) => f.name === name);

export const optionFor = (
  field: SchemaField | undefined,
  value: AttrValue,
): SchemaOption | undefined =>
  typeof value === "string"
    ? field?.options?.find((o) => o.value === value)
    : undefined;

// ---------------------------------------------------------------------------
// Dates
//
// Shared by display, validation and staleness, which is why they sit above all
// three: the same notion of "is this a date" has to answer for a value being
// rendered, a value being typed, and a value being aged.
// ---------------------------------------------------------------------------

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** True for a real calendar date, rejecting 2026-02-31 the way strptime does. */
function isRealDate(text: string): boolean {
  const [year, month, day] = text.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

export const EMPTY_DISPLAY = "—";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const clock = (date: Date): string => {
  const hours = date.getHours();
  const suffix = hours < 12 ? "am" : "pm";
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${String(date.getMinutes()).padStart(2, "0")}${suffix}`;
};

/**
 * A stored date as a person reads it: "12 Aug 2026", or "12 Aug 2026, 2:23pm"
 * for a value that carries a time.
 *
 * Two shapes reach this, and they are not the same kind of thing. A bare
 * `YYYY-MM-DD` (the lidar calibration) is a *calendar date* — it is formatted
 * from its own digits, never through `Date`, because parsing it as an instant
 * and rendering it in a western timezone moves it to the day before. A full
 * timestamp (`config_updated_at`) is an *instant*, so it is rendered in the
 * reader's own timezone, which is the only way "when was this last touched"
 * answers the question they asked.
 *
 * Returns null for anything that isn't a date, so callers can fall through to
 * the raw string rather than print "Invalid Date" over a stored value.
 */
export function formatDateValue(value: string): string | null {
  const text = value.trim();
  if (ISO_DATE.test(text)) {
    const [year, month, day] = text.split("-").map(Number);
    if (!isRealDate(text)) return null;
    return `${day} ${MONTHS[month - 1]} ${year}`;
  }
  const parsed = Date.parse(text);
  if (Number.isNaN(parsed)) return null;
  const date = new Date(parsed);
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${clock(date)}`;
}

/** Human-readable rendering of one stored attribute. */
export function formatAttr(field: SchemaField, value: AttrValue): string {
  if (value === null || value === undefined || value === "") {
    return EMPTY_DISPLAY;
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : EMPTY_DISPLAY;
  }
  if (field.type === "enum") {
    // Fall back to the raw value rather than blanking: a value stored before a
    // vocabulary change should stay visible, not vanish from the record.
    return optionFor(field, value)?.label ?? String(value);
  }
  if (field.type === "date" && typeof value === "string") {
    // Same fallback rule as the enum: an unparseable value is shown as stored
    // rather than hidden behind a formatting failure.
    return formatDateValue(value) ?? String(value);
  }
  return String(value);
}

/** The value as a form control should hold it. */
export function toDraft(field: SchemaField, value: AttrValue): DraftValue {
  if (field.type === "string_list") {
    return Array.isArray(value) ? [...value] : [];
  }
  if (value === null || value === undefined) return "";
  return String(value);
}

/** Seed a full draft for every editable field on a vehicle. */
export function draftFromVehicle(
  fields: SchemaField[],
  vehicle: Vehicle,
): Draft {
  const draft: Draft = {};
  for (const field of fields) {
    if (!isEditable(field)) continue;
    draft[field.name] = toDraft(field, vehicle.attrs[field.name] ?? null);
  }
  return draft;
}

// ---------------------------------------------------------------------------
// Normalisation and diffing
// ---------------------------------------------------------------------------

/**
 * A draft value as it will go on the wire.
 *
 * Mirrors `tractor_config.validate_patch`: strings are trimmed, and an empty
 * string or empty list becomes `null`, which the API turns into a DynamoDB
 * REMOVE. That equivalence has to happen *here* as well as server-side, or
 * clearing a field would look like a change from "" to null forever and a
 * whitespace-only edit would look dirty when it isn't.
 */
export function fromDraft(field: SchemaField, value: DraftValue): AttrValue {
  if (field.type === "string_list") {
    const items = (Array.isArray(value) ? value : [value])
      .map((entry) => entry.trim())
      .filter(Boolean);
    return items.length ? items : null;
  }
  const text = String(value).trim();
  if (!text) return null;
  if (field.type === "int") {
    const parsed = Number(text);
    return Number.isInteger(parsed) ? parsed : text;
  }
  return text;
}

/** Normalise a *stored* value the same way, so comparison is like-for-like. */
export function normalizeStored(
  field: SchemaField,
  value: AttrValue | undefined,
): AttrValue {
  if (value === null || value === undefined) return null;
  return fromDraft(field, toDraft(field, value));
}

export function attrEquals(a: AttrValue, b: AttrValue): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    const left = Array.isArray(a) ? a : [];
    const right = Array.isArray(b) ? b : [];
    return left.length === right.length && left.every((x, i) => x === right[i]);
  }
  return a === b;
}

export interface FieldChange {
  field: SchemaField;
  from: AttrValue;
  to: AttrValue;
  /**
   * Set on a change a cross-field rule will make, not the operator: the reason
   * in the schema's own words. Such a change is previewed in Review and never
   * sent — the backend applies it (see `ruleCascades`).
   */
  rule?: string;
}

/**
 * The dirty subset of a draft, as `{field: value}` plus a reviewable list.
 *
 * Only changed, editable fields are included. That is a correctness
 * requirement, not an optimisation: a PATCH is refused wholesale if it names
 * any field the caller may not write, so sending the untouched contents of a
 * form would let an admin-only field the operator never edited reject the
 * entire save.
 */
export function buildChanges(
  fields: SchemaField[],
  vehicle: Vehicle,
  draft: Draft,
): { changes: Record<string, AttrValue>; list: FieldChange[] } {
  const changes: Record<string, AttrValue> = {};
  const list: FieldChange[] = [];
  for (const field of fields) {
    if (!isEditable(field) || !(field.name in draft)) continue;
    const to = fromDraft(field, draft[field.name]);
    const from = normalizeStored(field, vehicle.attrs[field.name]);
    if (attrEquals(from, to)) continue;
    changes[field.name] = to;
    list.push({ field, from, to });
  }
  return { changes, list };
}

// ---------------------------------------------------------------------------
// Cross-field rules
//
// `ConfigSchema.rules` is the backend's own table ("production_ready must not
// be Yes while allocation is Customer PMI"), served so the console can act on
// it before the round trip: disable the option the write path would refuse,
// say why, and preview the value a save will flip. Nothing here decides a
// rule; a schema without `rules` gets none of this behaviour.
//
// Three readings of one table, for three moments:
//   * `blockedOptions` — while editing: which values of a field are refused on
//     the row as the draft leaves it, and why.
//   * `ruleErrors`     — on Review: the operator's *own* change into a conflict,
//     which the backend would 400. Caught here instead.
//   * `ruleCascades`   — on Review: a stored value the operator did *not*
//     touch that the backend will flip because of what they did change.
//     Shown as "applied by rule"; never put in the PATCH body, because the
//     operator may not be allowed to write that field and the save must still
//     go through.
// ---------------------------------------------------------------------------

export interface RuleConflictHit {
  name: string;
  field: SchemaField | undefined;
  value: string;
}

const conflictHits = (
  schema: ConfigSchema | null,
  rule: SchemaRule,
  effective: Record<string, AttrValue>,
): RuleConflictHit[] =>
  rule.conflicts.flatMap((c) => {
    const value = effective[c.field];
    return typeof value === "string" && c.values.includes(value)
      ? [{ name: c.field, field: fieldByName(schema, c.field), value }]
      : [];
  });

/** "Org allocation is Customer PMI; Run type is Offline" — the backend's wording. */
export const describeConflicts = (hits: RuleConflictHit[]): string =>
  hits
    .map(
      ({ name, field, value }) =>
        `${field?.label ?? name} is ${optionFor(field, value)?.label ?? value}`,
    )
    .join("; ");

/** The row as the draft leaves it: stored attrs with the operator's changes on top. */
export function effectiveAttrs(
  vehicle: Vehicle,
  list: FieldChange[],
): Record<string, AttrValue> {
  const row: Record<string, AttrValue> = { ...vehicle.attrs };
  for (const change of list) row[change.field.name] = change.to;
  return row;
}

/** Option value -> why a rule refuses it, for `field` on `effective`. */
export function blockedOptions(
  schema: ConfigSchema | null,
  field: SchemaField,
  effective: Record<string, AttrValue>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const rule of schema?.rules ?? []) {
    if (rule.field !== field.name) continue;
    const hits = conflictHits(schema, rule, effective);
    if (hits.length) out[rule.value] = describeConflicts(hits);
  }
  return out;
}

/** Changes the backend will make because of the operator's own, with the reason. */
export function ruleCascades(
  schema: ConfigSchema | null,
  vehicle: Vehicle,
  list: FieldChange[],
): FieldChange[] {
  const own = new Set(list.map((c) => c.field.name));
  const effective = effectiveAttrs(vehicle, list);
  const out: FieldChange[] = [];
  for (const rule of schema?.rules ?? []) {
    if (own.has(rule.field) || effective[rule.field] !== rule.value) continue;
    const field = fieldByName(schema, rule.field);
    if (!field) continue;
    const hits = conflictHits(schema, rule, effective);
    if (!hits.length) continue;
    out.push({
      field,
      from: effective[rule.field] ?? null,
      to: rule.fallback,
      rule: describeConflicts(hits),
    });
  }
  return out;
}

/** The operator's own changes a rule refuses, keyed by field name. */
export function ruleErrors(
  schema: ConfigSchema | null,
  vehicle: Vehicle,
  list: FieldChange[],
): Record<string, string> {
  const effective = effectiveAttrs(vehicle, list);
  const errors: Record<string, string> = {};
  for (const change of list) {
    for (const rule of schema?.rules ?? []) {
      if (rule.field !== change.field.name || change.to !== rule.value)
        continue;
      const hits = conflictHits(schema, rule, effective);
      if (!hits.length) continue;
      const label = optionFor(change.field, rule.value)?.label ?? rule.value;
      errors[change.field.name] =
        `Can’t be ${label} while ${describeConflicts(hits)}.`;
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Validation (type contract + max_length only — see the module docstring)
// ---------------------------------------------------------------------------

/** A message to show under the control, or null when it's acceptable. */
export function validateDraft(
  field: SchemaField,
  value: DraftValue,
): string | null {
  const normalized = fromDraft(field, value);
  if (normalized === null) return null; // Clearing is always allowed.

  if (Array.isArray(normalized)) {
    if (field.max_length) {
      const tooLong = normalized.find(
        (entry) => entry.length > (field.max_length as number),
      );
      if (tooLong) {
        return `Entries must be ${field.max_length} characters or fewer.`;
      }
    }
    return null;
  }

  if (field.type === "int") {
    return typeof normalized === "number" ? null : "Must be a whole number.";
  }

  const text = String(normalized);
  if (field.max_length && text.length > field.max_length) {
    return `${text.length} characters — the limit is ${field.max_length}.`;
  }
  if (field.type === "url" && !text.startsWith("https://")) {
    return "Must be an https:// URL.";
  }
  if (field.type === "date" && (!ISO_DATE.test(text) || !isRealDate(text))) {
    return "Must be a date in YYYY-MM-DD form.";
  }
  // Still a type-level rule, not vocabulary: a field the schema ages
  // (`stale_after_days`) records when something *was* done, and a date that
  // has not happened yet would read as fresh until it arrived.
  if (
    field.type === "date" &&
    field.stale_after_days &&
    text > new Date().toISOString().slice(0, 10)
  ) {
    return "Can’t be later than today.";
  }
  if (field.type === "enum" && !optionFor(field, text)) {
    return "Not one of the allowed values.";
  }
  return null;
}

/**
 * Every validation message in a draft, keyed by field name.
 *
 * `writable` says which fields this form is actually collecting — `isEditable`
 * for the edit panel, `canSetOnCreate` for onboarding. Reporting an error on a
 * field the form isn't collecting would block a save on a value the user cannot
 * reach.
 */
export function validateAll(
  fields: SchemaField[],
  draft: Draft,
  writable: (field: SchemaField) => boolean = isEditable,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (!writable(field) || !(field.name in draft)) continue;
    const message = validateDraft(field, draft[field.name]);
    if (message) errors[field.name] = message;
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------

/**
 * A roster name is a DynamoDB partition key *and* a URL path segment, so it is
 * constrained more tightly than any config value. Mirrors the API's own
 * `ROSTER_NAME` — a type-level contract, not vocabulary, so checking it here
 * saves a round trip without becoming a second copy of the field rules.
 */
export const ROSTER_NAME = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export function validateVehicleName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "A roster name is required.";
  if (!ROSTER_NAME.test(trimmed)) {
    return "Lower-case letters, digits, - or _, starting with a letter or digit (e.g. dl13).";
  }
  return null;
}

/** Fields the API requires when onboarding, taken from the schema. */
export const requiredOnCreate = (fields: SchemaField[]): SchemaField[] =>
  fields.filter((f) => f.required_on_create);

/**
 * The body for `POST /v1/vehicles`, from a draft.
 *
 * Unset fields are **omitted** rather than sent as null. On an edit a null means
 * "clear this attribute", which is meaningful; on a create there is nothing to
 * clear, so sending one would be noise in the change-set that records the
 * tractor coming into existence.
 */
export function buildCreatePayload(
  fields: SchemaField[],
  draft: Draft,
): Record<string, AttrValue> {
  const payload: Record<string, AttrValue> = {};
  for (const field of fields) {
    // The create question, not the edit one: identity may be supplied here and
    // nowhere else, so gating this on `can_edit` would drop it silently.
    if (!canSetOnCreate(field) || !(field.name in draft)) continue;
    const value = fromDraft(field, draft[field.name]);
    if (value !== null) payload[field.name] = value;
  }
  return payload;
}

/** Required fields still unset in a create draft. */
export function missingRequired(
  fields: SchemaField[],
  draft: Draft,
): SchemaField[] {
  return requiredOnCreate(fields).filter(
    (f) => fromDraft(f, draft[f.name] ?? "") === null,
  );
}

// ---------------------------------------------------------------------------
// Staleness
// ---------------------------------------------------------------------------

const MS_PER_DAY = 86_400_000;

/**
 * Whole days between a stored date and now, or null if it isn't a date.
 *
 * Handles both shapes the row carries: `lidar_calibration_date` is a bare
 * YYYY-MM-DD, `config_updated_at` is a full ISO timestamp.
 */
export function daysSince(value: AttrValue, now: number): number | null {
  if (typeof value !== "string" || !value) return null;
  const parsed = Date.parse(
    ISO_DATE.test(value) ? `${value}T00:00:00Z` : value,
  );
  if (Number.isNaN(parsed)) return null;
  return Math.floor((now - parsed) / MS_PER_DAY);
}

/** True when a field carries a threshold and its value has passed it. */
export function isStale(
  field: SchemaField,
  value: AttrValue,
  now: number,
): boolean {
  if (!field.stale_after_days) return false;
  const age = daysSince(value, now);
  return age !== null && age > field.stale_after_days;
}

/**
 * Whether the schema wants the freshness chrome drawn at all — ages, stale
 * badges, to-check counts. A schema that says nothing (an older backend)
 * reads as "on": display follows the schema, and an old schema never said to
 * hide anything. `freshnessFor` itself stays available either way — it is a
 * fact about the record, and Re-confirm keeps writing stamps so the history
 * exists whenever the chrome is turned on.
 */
export const freshnessEnabled = (schema: ConfigSchema | null): boolean =>
  schema?.features?.freshness ?? true;

export interface Freshness {
  /** Whole days since the relevant date, or null when there isn't one. */
  age: number | null;
  /** Past the field's declared threshold. */
  stale: boolean;
  /**
   * The field holds a value but carries no confirmation stamp. Only possible on
   * a `confirmable` field last written before the stamping existed — so it
   * means "nobody has re-confirmed this since", which on a safety approval is a
   * different and more actionable statement than merely being old.
   */
  unconfirmed: boolean;
}

/**
 * How fresh one field's value is.
 *
 * Which date to measure depends on the field. A plain date field (the lidar
 * calibration) ages from its own value. A `confirmable` approval ages from its
 * `_confirmed_at` sibling instead — the approval's *value* has no date in it,
 * and the row-level `config_updated_at` would say a tractor's autonomy sign-off
 * was fresh because somebody edited its notes this morning.
 */
export function freshnessFor(
  field: SchemaField,
  attrs: Record<string, AttrValue>,
  now: number,
): Freshness {
  const value = attrs[field.name] ?? null;
  const source =
    field.confirmable && field.confirmed_at_field
      ? (attrs[field.confirmed_at_field] ?? null)
      : value;
  const age = daysSince(source, now);
  return {
    age,
    stale:
      !!field.stale_after_days && age !== null && age > field.stale_after_days,
    unconfirmed: !!field.confirmable && value !== null && age === null,
  };
}

/** One field that is asking to be looked at, and what is wrong with it. */
export interface AttentionItem {
  field: SchemaField;
  /** `stale`: past its own threshold. `unconfirmed`: has a value, no stamp. */
  kind: "stale" | "unconfirmed";
}

/**
 * Everything on this tractor that wants attention, in schema order.
 *
 * One derivation, three readers: the chips at the top of the detail page, which
 * groups of the config record open by default, and (by absence) whether the
 * record is quiet. Deriving it once is what stops the chips from claiming three
 * stale approvals while the groups holding them sit shut.
 */
export function attentionItems(
  fields: SchemaField[],
  attrs: Record<string, AttrValue>,
  now: number,
): AttentionItem[] {
  const items: AttentionItem[] = [];
  for (const field of [...fields].sort((a, b) => a.order - b.order)) {
    const fresh = freshnessFor(field, attrs, now);
    if (fresh.stale) items.push({ field, kind: "stale" });
    else if (fresh.unconfirmed) items.push({ field, kind: "unconfirmed" });
  }
  return items;
}

/**
 * Whether this field can be re-affirmed at its current value right now.
 *
 * Three conditions, all from the contract the API enforces: the field is an
 * attestation, the caller may write it (re-affirming moves a safety clock, so it
 * takes the same permission as changing the value), and there is a value to
 * affirm — confirming an empty field would stamp freshness onto nothing, which
 * the API rejects with a 400. Checking here keeps the affordance and the
 * acceptance rule in agreement rather than offering a button that 400s.
 */
export function canConfirm(
  field: SchemaField,
  attrs: Record<string, AttrValue>,
): boolean {
  if (!field.confirmable || !isEditable(field)) return false;
  const value = attrs[field.name];
  return value !== null && value !== undefined && value !== "";
}

/** "3 days ago" / "today" / null when there is no date to describe. */
export function describeAge(age: number | null): string | null {
  if (age === null) return null;
  if (age <= 0) return "today";
  if (age === 1) return "yesterday";
  if (age < 60) return `${age} days ago`;
  const months = Math.round(age / 30);
  return months < 24
    ? `${months} months ago`
    : `${Math.round(age / 365)} years ago`;
}

// ---------------------------------------------------------------------------
// Change-sets
// ---------------------------------------------------------------------------

/**
 * Who made an edit, across both stored change-set shapes.
 *
 * Rows written before SW-6194 carry a single `actor`; rows written after carry
 * `actor_email` beside an immutable `actor_sub`. Both are in the table and
 * neither is derivable from the other, so this reads whichever exists. The sub
 * is the last resort rather than the first because it is opaque to a human —
 * it is the right key for an audit join, not the right thing to show in a list.
 */
export const changeSetActor = (entry: ChangeSet): string =>
  entry.actor_email || entry.actor || entry.actor_sub || "unknown";

/** Stable list key. Pre-SW-6194 change-sets have no id; fall back to position. */
export const changeSetKey = (entry: ChangeSet, index: number): string =>
  entry.id ?? `${entry.at}#${index}`;

/** Newest first — the row stores them append-only, so oldest first. */
export const orderedHistory = (history: ChangeSet[]): ChangeSet[] =>
  [...history].reverse();

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

/**
 * Resolve a URL segment to a roster name.
 *
 * The console used to key its detail route on the Jira asset id, so links of
 * the form `/tractor-state/DL-13` exist in Slack threads and bookmarks. Those
 * still resolve — via the loaded fleet, never via a hardcoded mapping, since
 * the roster name is not derivable from the Jira id (`av6` is AV-DL-01 while
 * `dl6` is AV-DL-06) and a third copy of that table is exactly what the
 * migration is removing.
 */
export function resolveVehicleName(
  param: string | undefined,
  vehicles: Vehicle[],
): string | null {
  if (!param) return null;
  const wanted = param.toLowerCase();
  const match =
    vehicles.find((v) => v.vehicleName === param) ??
    vehicles.find((v) => v.vehicleName.toLowerCase() === wanted) ??
    vehicles.find((v) => (v.tractorId ?? "").toLowerCase() === wanted) ??
    vehicles.find((v) => (v.displayName ?? "").toLowerCase() === wanted);
  return match?.vehicleName ?? null;
}

/**
 * The fields whose values move the production-ready lane on Tractor Metrics
 * (SW-8879): the mark itself and the rule table's blockers. A change to one
 * of them travels onto the timeline, so it needs the note that says why.
 */
export const READINESS_FIELDS: readonly string[] = [
  "production_ready",
  "allocation",
  "run_type",
  "status",
];

export const touchesReadiness = (names: Iterable<string>): boolean =>
  Array.from(names).some((name) => READINESS_FIELDS.includes(name));

/** "35 min", "2 h 10 min", "1 d 19 h" — how far back a change was dated. */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d >= 1) return h ? `${d} d ${h} h` : `${d} d`;
  if (h >= 1) return m ? `${h} h ${m} min` : `${h} h`;
  return `${Math.max(m, 1)} min`;
}
